/*
 * useVoxPontBetaAccess — real-time check of the current user's VoxPont Beta
 * access.
 *
 * Reads voxpontBetaUsage/{uid} via the compat SDK and subscribes with
 * onSnapshot. Keyed on the authenticated uid from useAuth so it resubscribes
 * correctly once auth resolves (and on page reload) rather than reading
 * currentUser a single time.
 *
 * NOTE: Once VoxPont billing is live, this is intended to be OR'd with an
 * active subscription check — either active beta access OR active subscription
 * grants entry to /voxpont4.
 */
import { useEffect, useState } from "react";
import { useAuth } from "hooks/useAuth";
import { subscribeToVoxPontBetaUsage } from "services/voxpontBeta";

/**
 * @returns {{
 *   status: 'loading' | 'active' | 'expired' | 'none',
 *   usage: object | null
 * }}
 *   'active'  — beta access exists and expiresAt is in the future
 *   'expired' — beta access exists but expiresAt has passed
 *   'none'    — no beta access doc found for this user
 *   'loading' — auth or the access check is in progress
 */
export function useVoxPontBetaAccess() {
  const { user, userLoading } = useAuth();
  const [status, setStatus] = useState("loading");
  const [usage, setUsage] = useState(null);

  const uid = user?.uid ?? null;
  const isAnonymous = Boolean(user?.isAnonymous);

  useEffect(() => {
    if (userLoading) {
      setStatus("loading");
      return undefined;
    }

    if (!uid || isAnonymous) {
      setStatus("none");
      setUsage(null);
      return undefined;
    }

    setStatus("loading");
    const unsub = subscribeToVoxPontBetaUsage(uid, (data) => {
      if (!data) {
        setUsage(null);
        setStatus("none");
        return;
      }
      const now = Date.now();
      const expiresMs = data.expiresAt?.toMillis?.() ?? 0;
      setUsage(data);
      setStatus(expiresMs > now ? "active" : "expired");
    });

    return () => unsub();
  }, [uid, isAnonymous, userLoading]);

  return { status, usage };
}

export default useVoxPontBetaAccess;
